/**
 * Fences, walls and the rest of what divides one piece of ground from another.
 *
 * The same question the entrances answer from the other side: a door tells you
 * where you can get in, a fence tells you where you cannot. 1,937 ways
 * downtown; 57,046 across the whole box, which is why this reads tiles now —
 * shipping the citywide set whole would be an 11 MB payload for a layer that
 * only draws above z15, where a handful of tiles cover the screen.
 *
 * Drawn at their own height rather than flat on the ground, so in a pitched
 * view they read as things standing in the world alongside the buildings. Few
 * carry a surveyed height; the rest use a typical figure for that kind of
 * barrier, and the tooltip says which it is looking at.
 */

import { SolidPolygonLayer } from "@deck.gl/layers";

import { pmTileLayer, tileFeatures } from "../map/tiles.js";

import { fencePanels } from "./solids.js";

import { Layer } from "./base.js";
import { palette, themeName } from "../theme/palette.js";
import { DEPTH_TESTED } from "../map/depth.js";

const LABEL = {
  fence: "Fence",
  wall: "Wall",
  retaining_wall: "Retaining wall",
  hedge: "Hedge",
  jersey_barrier: "Concrete barrier",
  guard_rail: "Guard rail",
};

/** Below street zoom these are hair-thin clutter over every property line. */
const BARRIER_MIN_ZOOM = 15;

/**
 * A tile feature's line parts, whichever of the two line types it parsed as.
 *
 * splitMultiPolygons in map/tiles.js normalises polygons only; a barrier way
 * that MVT encoding split arrives as a MultiLineString and must not be read as
 * a single path — joining its parts would draw phantom panels between them.
 */
function linePaths(geometry) {
  if (geometry?.type === "LineString") return [geometry.coordinates];
  if (geometry?.type === "MultiLineString") return geometry.coordinates;
  return [];
}

export class BarrierLayer extends Layer {
  static key = "barriers";
  static label = "Fences";
  static description = "Fences, walls and hedges from OSM";

  /** Where the tiled version of this data lives, if it has been built. */
  tileUrl = "./data/barriers.pmtiles";

  /**
   * Set by the app when the archive is present, like LandUseLayer.tiled.
   *
   * When it is not — a deploy that forgot build-tiles.sh — the layer falls
   * back to the downtown JSON from /api/barriers rather than going dark.
   */
  tiled = false;

  /**
   * Tiled, the zoom gate lives on the tileset's minZoom and deck keeps it
   * current without any repaint; only the whole-file fallback still gates in
   * render() and needs the crossing repaint from main.js.
   */
  get zoomThresholds() {
    return this.tiled ? [] : [BARRIER_MIN_ZOOM];
  }

  async load() {
    if (this.tiled) {
      // Nothing to fetch: the geometry arrives tile by tile as the map moves.
      this.status = "ready";
      this.detail = "tiled";
      return null;
    }
    this.status = "loading";
    try {
      const response = await fetch(`${this.basePath}/barriers`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._barriers = body.barriers ?? [];
      this.status = this._barriers.length ? "ready" : "unavailable";
      this.detail = `${this._barriers.length} mapped`;
      return null;
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  get loadedCount() {
    return this._barriers?.length ?? 0;
  }

  legend() {
    return [{
      rgb: palette().entranceInferred,
      label: "Fence / wall",
      note: "What you cannot walk through. Heights are typical unless tagged.",
    }];
  }

  describe(picked) {
    // What deck picks here is a fence PANEL — one segment's footprint — whose
    // `source` is the record it came from. From the API that record is flat
    // (kind, height, surveyed, material); from a tile it is a GeoJSON feature
    // with those under `properties`. Read through both, or the tooltip calls
    // everything "Barrier" and loses the material — which is exactly what the
    // old panel-blind version did.
    const source = picked?.source ?? picked ?? {};
    const props = source.properties ?? source;

    const rows = [["Type", LABEL[props.kind] ?? "Barrier"]];
    rows.push(["Height", props.surveyed
      ? `${props.height} m`
      : `~${props.height} m — typical, not surveyed`]);
    if (props.material) {
      rows.push(["Material", String(props.material).replace(/_/g, " ")]);
    }
    rows.push(["Source", "OpenStreetMap"]);
    return { title: LABEL[props.kind] ?? "Barrier", rows };
  }

  /**
   * The shared panel style, for both the tiled and the whole-file path.
   *
   * A fence, not a line hovering at fence height: each segment is a thin
   * footprint extruded from the ground to the fence's own height, so it
   * occludes what is behind it and takes the same light as everything else.
   * A hedge keeps its own colour; everything else is fence-grey.
   */
  panelStyle() {
    const grey = palette().entranceInferred;
    const green = palette().tree;
    return {
      extruded: true,
      filled: true,
      getPolygon: (d) => d.polygon,
      getElevation: (d) => d.height,
      getFillColor: (d) => (d.hedge ? [...green.slice(0, 3), 225] : [...grey, 205]),
      // The greys above are captured when this style is built; the trigger is
      // what makes a theme switch rebuild the fill on panels already drawn.
      updateTriggers: { getFillColor: themeName() },
      pickable: true,
      parameters: DEPTH_TESTED,
    };
  }

  /**
   * Tiled barriers: the citywide set, fetched by viewport.
   *
   * The zoom gate is `minZoom` on the TileLayer, NOT a check in render():
   * nothing repaints the feeds slot on zoom (moveend rebuilds only doors,
   * sheds and entrances — see rebuildStatic in main.js), so a gate computed
   * at render time goes stale until the next full paint, which can be the
   * one-minute refresh tick. deck re-evaluates minZoom on every viewport
   * change without a rebuild: below it the tileset selects no tiles (measured
   * in deck's getTileIndices — no `extent`, so `z < minZoom` returns []), and
   * the moment the viewport crosses it the tiles come in, cache intact.
   */
  tileLayer() {
    const style = this.panelStyle();
    const theme = themeName();

    return pmTileLayer({
      id: `${this.key}-tiles`,
      url: this.tileUrl,
      visible: this.enabled,
      // With tileSize 512 deck asks for tile z = round(viewport zoom), so
      // minZoom 15 starts drawing at viewport ~14.5 — half a notch under the
      // old z15 gate, where the panels are a pixel tall and read as texture
      // rather than clutter. The archive holds z14–16; overzoom covers 16+.
      minZoom: 15,
      maxZoom: 16,
      // Parents stop drawing once their children are in, and panning cannot
      // accumulate an unbounded tessellation backlog — same reasoning, at
      // smaller scale, as the buildings tile layer.
      refinementStrategy: "no-overlap",
      maxCacheSize: 48,
      updateTriggers: { renderSubLayers: theme },
      renderSubLayers: (props) => {
        const features = tileFeatures(props);
        if (!features?.length) return null;

        const panels = [];
        for (const feature of features) {
          const p = feature.properties ?? {};
          const hedge = p.kind === "hedge";
          const height = Number(p.height) || 1.5;
          for (const path of linePaths(feature.geometry)) {
            for (const polygon of fencePanels(path)) {
              panels.push({ polygon, height, hedge, source: feature });
            }
          }
        }
        if (!panels.length) return null;

        return new SolidPolygonLayer(props, {
          id: `${props.id}-panels`,
          data: panels,
          ...style,
        });
      },
    });
  }

  render(now, zoom = 16) {
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }
    if (this.tiled) return [this.tileLayer()];

    if (zoom < BARRIER_MIN_ZOOM) {
      this.visibleCount = 0;
      return [];
    }
    const data = this._barriers;
    this.visibleCount = data.length;

    // One row per segment, flattened out of the paths.
    const panels = [];
    for (const barrier of data) {
      const hedge = barrier.kind === "hedge";
      for (const polygon of fencePanels(barrier.path)) {
        panels.push({ polygon, height: barrier.height, hedge, source: barrier });
      }
    }

    return [
      new SolidPolygonLayer({
        id: `${this.key}-panels`,
        data: panels,
        ...this.panelStyle(),
      }),
    ];
  }
}
